import React, { useState, useEffect, useCallback } from 'react';
import { PublicLayout, Reveal } from '@/components/public';
import { BRAND } from '@/config/brand';
import { INTEGRATIONS_JSON_LD_STRING } from '@/config/integrations';
import { SOLUTIONS_FOOTER_COLUMN } from '@/config/verticals';
import IntegrationsHero from './integrations/IntegrationsHero';
import StripeSection from './integrations/StripeSection';
import HardwareSection from './integrations/HardwareSection';
import PosMatrixSection from './integrations/PosMatrixSection';
import PosDetailCards from './integrations/PosDetailCards';
import OrderFlowDiagram from './integrations/OrderFlowDiagram';
import IntegrationsFaq from './integrations/IntegrationsFaq';

const PAGE_TITLE = 'POS, Stripe & Thermal Printer Integrations for Canadian Restaurants | Hungry Canadian';
const PAGE_DESCRIPTION =
  'Connect Hungry Canadian with Shopify POS, Lightspeed, Square, Clover, Deliverect, Stripe payments, and Epson or Star Micronics thermal printers.';

const NAV_PROPS = {
  links: [
    { href: '#stripe-payments', label: 'Payments', testId: 'integrations-nav-payments' },
    { href: '#thermal-printers', label: 'Hardware', testId: 'integrations-nav-hardware' },
    { href: '#pos-matrix', label: 'Compare POS', testId: 'integrations-nav-matrix' },
    { href: '#integrations-faq', label: 'FAQ', testId: 'integrations-nav-faq' },
  ],
  primaryCta: { href: '/', label: 'Become a partner', testId: 'integrations-nav-join-cta' },
};

const FOOTER_PROPS = {
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
  columns: [
    {
      title: 'For Restaurants',
      links: [
        { href: '/', label: 'Why Hungry Canadian', testId: 'integrations-footer-why' },
        { href: '/tools', label: 'Tools', testId: 'integrations-footer-tools' },
        { href: '/features', label: 'Features', testId: 'integrations-footer-features' },
        { href: '/integrations', label: 'Integrations', testId: 'integrations-footer-integrations' },
      ],
    },
    SOLUTIONS_FOOTER_COLUMN,
    {
      title: 'Resources',
      links: [
        { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'integrations-footer-help' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'integrations-footer-contact' },
      ],
    },
    {
      title: 'Company',
      links: [{ href: BRAND.website, label: 'Main site', external: true, testId: 'integrations-footer-main' }],
    },
  ],
  legal: [
    { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'integrations-footer-tos' },
    { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'integrations-footer-privacy' },
  ],
};

export default function IntegrationsPage() {
  const [selectedPosId, setSelectedPosId] = useState(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (prevDescription != null) meta.setAttribute('content', prevDescription);
    };
  }, []);

  const handleViewDetails = useCallback((id) => {
    setSelectedPosId(id);
    requestAnimationFrame(() => {
      document.getElementById(`pos-card-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: INTEGRATIONS_JSON_LD_STRING }}
      />

      <div data-testid="integrations-page">
        <IntegrationsHero />
        <StripeSection />
        <HardwareSection />
        <PosMatrixSection onViewDetails={handleViewDetails} />
        <PosDetailCards selectedId={selectedPosId} />
        <OrderFlowDiagram />
        <IntegrationsFaq />

        {/* Compliance disclosure */}
        <section className="bg-background border-t border-border px-4 sm:px-6 py-8" data-testid="integrations-disclosure">
          <Reveal>
            <p className="max-w-4xl mx-auto text-center text-[11px] md:text-xs text-muted-foreground/80 leading-relaxed">
              Disclosure: Hungry Canadian is an independent software platform integrating with
              Stripe, Epson, Star Micronics, Shopify, Lightspeed, Square, Clover, and Deliverect.
              All product names, logos, and trademarks belong to their respective owners. Referral
              links may be updated to reflect official partner programs.
            </p>
          </Reveal>
        </section>
      </div>
    </PublicLayout>
  );
}
